import React from 'react';

interface SliderProps {
  label: string;
  value: number | string; // Accept both number and string for PostgreSQL compatibility
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  className?: string;
  valueLabels?: string[]; // Optional array of labels for each value
}

const Slider: React.FC<SliderProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  disabled = false,
  className = '',
  valueLabels
}) => {
  // Helper function to safely convert value to number
  const getNumericValue = (val: number | string): number => {
    if (typeof val === 'number') return val;
    const parsed = parseFloat(val);
    return isNaN(parsed) ? min : parsed;
  };

  const numericValue = getNumericValue(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };

  const getDisplayValue = () => {
    if (valueLabels && valueLabels[numericValue - min]) {
      return valueLabels[numericValue - min];
    }
    if (step === 1) {
      return numericValue.toString();
    }
    return numericValue.toFixed(2).replace(/\.?0+$/, ''); // Remove trailing zeros
  };

  const getDisplayLabel = () => {
    if (valueLabels && valueLabels[numericValue - min]) {
      return `${label.split('(')[0].trim()} (${valueLabels[numericValue - min]})`;
    }
    return label;
  };

  const getNumericDisplay = () => {
    if (valueLabels) {
      // For labeled sliders, still show the numeric value
      return `${numericValue}/${max}`;
    }
    return `${getDisplayValue()}/${max}`;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {getDisplayLabel()}
        </label>
        <span className="text-sm font-medium text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          {getNumericDisplay()}
        </span>
      </div>
      
      <div className="relative w-full h-2 rounded-lg">
        {/* Base track */}
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-lg" />

        {/* Progress fill */}
        <div 
          className="absolute top-0 left-0 h-full bg-blue-500 rounded-lg pointer-events-none"
          style={{ width: `${((numericValue - min) / (max - min)) * 100}%` }}
        />

        {/* Range input (transparent track, only thumb visible) */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={numericValue}
          onChange={handleChange}
          disabled={disabled}
          className="absolute top-0 left-0 w-full h-2 bg-transparent appearance-none cursor-pointer slider-thumb"
        />
      </div>
      
      {/* Scale markers */}
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{min}</span>
        {Array.from({ length: Math.floor((max - min) / step) - 1 }, (_, i) => {
          const markerValue = min + (i + 1) * step;
          if (step === 1 || markerValue % 1 === 0) {
            return <span key={markerValue}>{markerValue}</span>;
          }
          return null;
        })}
        <span>{max}</span>
      </div>
    </div>
  );
};

export default Slider;
